// Middleware
import AuthMiddleware from '@middleware/AuthMiddleware';
import SectionMiddleware from '@middleware/SectionMiddleware';
import SubAccountMiddleware from '@middleware/SubAccountMiddleware';

// Layouts
import LayoutApp from '@layouts/LayoutApp';

export default {
    path: '/',
    component: LayoutApp,
    children: [
        {
            path: 'access-denied',
            name: 'gate',
            component: () => import('@pages/gate'),
            meta: {
                middleware: AuthMiddleware,
            }
        },
        {
            path: 'projects/:id/:slug(.*)?',
            name: 'project',
            component: () => import('@pages/project'),
            meta: {
                middleware: AuthMiddleware,
                title: 'titles.redirecting',
            }
        },
        {
            path: 'dashboard',
            name: 'dashboard',
            component: () => import('@pages/dashboard'),
            meta: {
                middleware: AuthMiddleware,
                title: 'titles.dashboard',
            }
        },
        {
            path: 'customers',
            name: 'customers',
            component: () => import('@pages/customers'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.customers',
            }
        },
        {
            path: 'customers/:id',
            name: 'customers.show',
            component: () => import('@pages/customers-show'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.customer',
            }
        },
        {
            path: 'messages',
            name: 'messages',
            component: () => import('@pages/messages'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.messages',
            }
        },
        {
            path: 'reviews',
            name: 'reviews',
            component: () => import('@pages/reviews'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.reviews',
            }
        },
        {
            path: 'reviews/:id',
            name: 'reviews.show',
            component: () => import('@pages/reviews-show'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.review',
            }
        },
        {
            path: 'review-pages',
            name: 'forms',
            component: () => import('@pages/forms'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.review_pages',
            }
        },
        {
            path: 'review-pages/:id',
            name: 'forms.show',
            component: () => import('@pages/forms-show'),
            redirect: {
                name: 'forms.show.settings'
            },
            children: [
                {
                    path: 'settings',
                    name: 'forms.show.settings',
                    component: () => import('@pages/forms-show/PageFormSettings'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.settings'],
                    },
                },
                {
                    path: 'appearance',
                    name: 'forms.show.appearance',
                    component: () => import('@pages/forms-show/PageFormAppearance'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.appearance'],
                    },
                },
                {
                    path: 'assistant',
                    redirect: { name: 'forms.show.review-suggestions' },
                },
                {
                    path: 'suggestions',
                    redirect: { name: 'forms.show.review-suggestions' },
                },
                {
                    path: 'review-suggestions',
                    name: 'forms.show.review-suggestions',
                    component: () => import('@pages/forms-show/PageFormAssistant'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.suggestions'],
                    },
                },
                {
                    path: 'editor',
                    name: 'forms.show.editor',
                    component: () => import('@pages/forms-show/PageFormEditor'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.code_editor'],
                    },
                },
                {
                    path: 'links',
                    name: 'forms.show.links',
                    component: () => import('@pages/forms-show/PageFormLinks'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.links'],
                    },
                },
                {
                    path: 'redirects',
                    name: 'forms.show.redirects',
                    component: () => import('@pages/forms-show/PageFormRedirects'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.redirects'],
                    },
                },
                {
                    path: 'fields',
                    name: 'forms.show.fields',
                    component: () => import('@pages/forms-show/PageFormFields'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.fields'],
                    },
                },
                {
                    path: 'success',
                    name: 'forms.show.success',
                    component: () => import('@pages/forms-show/PageFormSuccess'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.success_page'],
                    },
                },
                {
                    path: 'qr-codes',
                    name: 'forms.show.qr-codes',
                    component: () => import('@pages/forms-show/PageFormQr'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.qr_codes'],
                    },
                },
                {
                    path: 'locations',
                    name: 'forms.show.locations',
                    component: () => import('@pages/forms-show/PageFormLocations'),
                    meta: {
                        middleware: [AuthMiddleware, SectionMiddleware],
                        title: ['titles.review_page', 'labels.locations'],
                    },
                },
            ]
        },
        {
            path: 'widgets',
            name: 'widgets',
            component: () => import('@pages/widgets'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.widgets',
            }
        },
        {
            path: 'widgets/:id',
            name: 'widgets.show',
            component: () => import('@pages/widgets-show'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.widget',
            }
        },
        {
            path: 'exports/:uuid',
            name: 'export',
            component: () => import('@pages/export'),
            meta: {
                middleware: AuthMiddleware,
                title: 'titles.export',
            }
        },
        {
            path: 'analytics',
            name: 'analytics',
            component: () => import('@pages/analytics'),
            meta: {
                middleware: [AuthMiddleware, SectionMiddleware],
                title: 'titles.analytics',
            }
        },
        {
            path: 'referrals',
            name: 'referrals',
            component: () => import('@pages/referrals'),
            meta: {
                middleware: [AuthMiddleware, SubAccountMiddleware],
                title: 'labels.refer_and_earn',
            }
        },
        {
            path: 'referrals/referrals',
            name: 'referrals.referrals',
            component: () => import('@pages/referrals/referrals'),
            meta: {
                middleware: [AuthMiddleware, SubAccountMiddleware],
                title: ['labels.refer_and_earn', 'labels.referrals'],
            }
        },
        {
            path: 'referrals/commissions',
            name: 'referrals.commissions',
            component: () => import('@pages/referrals/commissions'),
            meta: {
                middleware: [AuthMiddleware, SubAccountMiddleware],
                title: ['labels.refer_and_earn', 'titles.referrals_commissions'],
            }
        },
        {
            path: 'referrals/payouts',
            name: 'referrals.payouts',
            component: () => import('@pages/referrals/payouts'),
            meta: {
                middleware: [AuthMiddleware, SubAccountMiddleware],
                title: ['labels.refer_and_earn', 'titles.referrals_payouts'],
            }
        },
    ]
}